import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!token) {
      setStatus("error");
      setError("No verification token found in the link.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(async () => {
        await refresh();
        setStatus("success");
        setTimeout(() => navigate("/competitions"), 2000);
      })
      .catch((e) => {
        const detail = formatApiError(e.response?.data?.detail);
        if (typeof detail === "string" && detail.includes("already been used")) {
          // Token consumed on a previous visit — account is verified
          setStatus("success");
          setTimeout(() => navigate("/competitions"), 2000);
          return;
        }
        setStatus("error");
        setError(detail);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="verify-email-page">
      <div className="w-full max-w-md ufr-card rounded-md p-8 text-center">
        <div className="flex justify-center mb-6"><Logo size={56} /></div>
        {status === "verifying" && (
          <div data-testid="verify-status-loading">
            <Loader2 size={40} className="text-[color:var(--ufr-green)] animate-spin mx-auto" />
            <h1 className="font-display text-2xl text-white mt-4">Verifying your email…</h1>
          </div>
        )}
        {status === "success" && (
          <div data-testid="verify-status-success">
            <CheckCircle2 size={44} className="text-[color:var(--ufr-green)] mx-auto" />
            <h1 className="font-display text-3xl text-white mt-4">You're verified! ⚽</h1>
            <p className="text-white/70 text-sm mt-3">
              You're now logged in — taking you to the live raffles…
            </p>
            <Link to="/competitions" className="btn-ufr-primary px-8 py-3 text-sm inline-block mt-6" data-testid="verify-browse-btn">
              Browse raffles now
            </Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-status-error">
            <XCircle size={44} className="text-red-400 mx-auto" />
            <h1 className="font-display text-2xl text-white mt-4">Verification failed</h1>
            <p className="text-white/70 text-sm mt-3">{error}</p>
            <p className="text-white/50 text-xs mt-4">
              Need a new link? Try logging in — we'll offer to resend it.
            </p>
            <Link to="/login" className="btn-ufr-ghost px-6 py-2.5 text-xs inline-block mt-5">Go to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
